// COPIA da TMA#718 di CriGio @ 18f1ef07d0fe7bf6a63c2730ac049e52c705f67e: src/lib/burraco/effetti.ts
/**
 * Le PRIMITIVE degli effetti del tavolo: scintille, anello d'oro, coriandoli, lampo, scritta centrale, punti in volo, fascia del turno.
 * Solo PRESENTAZIONE e solo DOM: elementi `position: fixed` nel `body`, animati con la Web Animations API (trasformazioni e opacita: niente layout),
 * tolti alla fine. Le misure (i rettangoli) le passa chi chiama: qui non si misura niente.
 * Gli effetti di dominio del burraco (la scritta, la calata, la pescata in vetrina, il jolly) restano in TMA#718 e non sono qui.
 */

export type Punto = { x: number; y: number };
export type Rettangolo = { left: number; top: number; width: number; height: number };

const CORIANDOLI = ['#ecd39a', '#d4af6a', 'var(--color-brand)', '#ff6f91', '#7fd4ff', '#ffffff', '#b8ff9a'];

/**
 * Il CASO degli effetti (dove cade una scintilla, quanto gira un coriandolo): un generatore semplice, NON crittografico e NON per il gioco. Non usa il caso del
 * linguaggio (niente che sembri un seed); qui serve solo all'aspetto, e un seme dall'orologio basta.
 */
let statoCaso = (Date.now() ^ 0x9e3779b9) >>> 0;
function casuale(): number {
  statoCaso = (statoCaso + 0x6d2b79f5) >>> 0;
  let t = statoCaso;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

const centroDi = (r: Rettangolo): Punto => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

function nuovo(stile: Partial<CSSStyleDeclaration>): HTMLDivElement {
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.dataset.effetto = '';
  Object.assign(el.style, { position: 'fixed', pointerEvents: 'none', zIndex: '955', ...stile });
  document.body.appendChild(el);
  return el;
}

function anima(el: HTMLElement, fotogrammi: Keyframe[], opzioni: KeyframeAnimationOptions) {
  const a = el.animate(fotogrammi, { fill: 'both', ...opzioni });
  a.onfinish = () => el.remove();
  a.oncancel = () => el.remove();
  return a;
}

/** Scintille che schizzano fuori da `centro`: `n` pezzi, fino a `raggio` px, coi colori dati. Le piu grandi sono stelline a quattro punte. */
export function scintille(centro: Punto, n: number, raggio: number, colori: readonly string[], ritardo = 0) {
  for (let i = 0; i < n; i++) {
    const angolo = (Math.PI * 2 * i) / n + casuale() * 0.6;
    const dist = raggio * (0.45 + casuale() * 0.55);
    const lato = 3 + casuale() * 5;
    const stella = lato > 6;
    const colore = colori[i % colori.length]!;
    const el = nuovo({
      left: `${centro.x - lato / 2}px`,
      top: `${centro.y - lato / 2}px`,
      width: `${lato}px`,
      height: `${lato}px`,
      background: colore,
      borderRadius: stella ? '1px' : '50%',
      clipPath: stella ? 'polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%)' : '',
      boxShadow: `0 0 ${lato * 1.6}px ${colore}`,
    });
    const dx = Math.cos(angolo) * dist;
    const dy = Math.sin(angolo) * dist;
    anima(
      el,
      [
        { transform: 'translate(0,0) scale(0.4) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx * 0.7}px, ${dy * 0.7}px) scale(1.3) rotate(120deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy + 18}px) scale(0.2) rotate(220deg)`, opacity: 0 },
      ],
      { duration: 650 + casuale() * 450, delay: ritardo + casuale() * 90, easing: 'cubic-bezier(.15,.7,.3,1)' },
    );
  }
}

/** Un anello d'oro che si allarga dal gioco calato. */
export function anelloDOro(r: Rettangolo, ritardo = 0, spessore = 2) {
  const c = centroDi(r);
  const lato = Math.max(r.width, r.height) + 16;
  const el = nuovo({
    left: `${c.x - lato / 2}px`,
    top: `${c.y - lato / 2}px`,
    width: `${lato}px`,
    height: `${lato}px`,
    borderRadius: '50%',
    border: `${spessore}px solid #ecd39a`,
    boxShadow: '0 0 18px rgba(236,211,154,.6), inset 0 0 12px rgba(236,211,154,.4)',
  });
  anima(el, [{ transform: 'scale(0.4)', opacity: 0.95 }, { transform: 'scale(2.4)', opacity: 0 }], { duration: 750, delay: ritardo, easing: 'cubic-bezier(.2,.7,.3,1)' });
}

/** Coriandoli che cadono dall'alto dello schermo, per un burraco. */
export function coriandoli(n: number, ritardo = 0) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  for (let i = 0; i < n; i++) {
    const x = casuale() * w;
    const lw = 5 + casuale() * 5;
    const lh = 8 + casuale() * 8;
    const el = nuovo({
      left: `${x}px`,
      top: '-20px',
      width: `${lw}px`,
      height: `${lh}px`,
      background: CORIANDOLI[i % CORIANDOLI.length]!,
      borderRadius: '2px',
    });
    const deriva = (casuale() - 0.5) * 160;
    const giri = 360 + casuale() * 720;
    anima(
      el,
      [
        { transform: 'translate(0,0) rotate(0deg) rotateY(0deg)', opacity: 1 },
        { transform: `translate(${deriva * 0.6}px, ${h * 0.55}px) rotate(${giri * 0.6}deg) rotateY(540deg)`, opacity: 1, offset: 0.6 },
        { transform: `translate(${deriva}px, ${h + 40}px) rotate(${giri}deg) rotateY(900deg)`, opacity: 0.9 },
      ],
      { duration: 1700 + casuale() * 1300, delay: ritardo + casuale() * 500, easing: 'cubic-bezier(.25,.45,.45,.95)' },
    );
  }
}

/** Un lampo di luce calda su tutto lo schermo, appena accennato. */
export function lampo(ritardo = 0, intensita = 0.28) {
  const el = nuovo({ inset: '0', background: 'radial-gradient(circle at 50% 45%, rgba(255,236,180,.9), rgba(255,236,180,0) 65%)' });
  anima(el, [{ opacity: 0 }, { opacity: intensita, offset: 0.25 }, { opacity: 0 }], { duration: 650, delay: ritardo, easing: 'ease-out' });
}

/**
 * COPIA MODIFICATA di `scrittaBurraco` (TMA#718 di CriGio @ 8e60f982, src/lib/burraco/effetti.ts): la scritta grande che entra al centro dello schermo e svanisce,
 * con il testo dato da chi chiama. CAMBI: si chiama `scrittaCentrale`, non ha il parametro `pulito` (resta la variante oro, quella del burraco non pulito), e nessuna
 * parola del burraco nel nome o nel commento.
 */
export function scrittaCentrale(testo: string, ritardo = 0) {
  const el = nuovo({
    left: '50%',
    top: '38%',
    padding: '10px 22px',
    borderRadius: '16px',
    whiteSpace: 'nowrap',
    fontFamily: "Georgia, 'Noto Serif', 'Times New Roman', serif",
    fontWeight: '700',
    fontSize: '30px',
    letterSpacing: '.02em',
    color: '#1b1405',
    background: 'linear-gradient(120deg, #f6e7bf, #d4af6a 50%, #f6e7bf)',
    backgroundSize: '200% 100%',
    boxShadow: '0 12px 30px rgba(0,0,0,.55), 0 0 0 2px #8f6d33, 0 0 30px rgba(236,211,154,.55)',
    textShadow: '0 1px 0 rgba(255,255,255,.5)',
  });
  el.textContent = testo;
  anima(
    el,
    [
      { transform: 'translate(-50%, -50%) scale(0.3) rotate(-8deg)', opacity: 0, backgroundPosition: '0% 0' },
      { transform: 'translate(-50%, -50%) scale(1.12) rotate(2deg)', opacity: 1, offset: 0.18 },
      { transform: 'translate(-50%, -50%) scale(1) rotate(0deg)', opacity: 1, offset: 0.3 },
      { transform: 'translate(-50%, -54%) scale(1) rotate(0deg)', opacity: 1, offset: 0.82, backgroundPosition: '100% 0' },
      { transform: 'translate(-50%, -70%) scale(0.92) rotate(0deg)', opacity: 0, backgroundPosition: '100% 0' },
    ],
    { duration: 1900, delay: ritardo, easing: 'cubic-bezier(.25,.8,.3,1)' },
  );
}

/** I punti guadagnati: un «+N» che sale dal punteggio di chi li ha fatti e svanisce. */
export function puntiInVolo(r: Rettangolo, testo: string, colore: string, ritardo = 0) {
  const c = centroDi(r);
  const el = nuovo({
    left: `${c.x}px`,
    top: `${c.y}px`,
    fontFamily: "Georgia, 'Noto Serif', 'Times New Roman', serif",
    fontWeight: '700',
    fontSize: '22px',
    color: colore,
    textShadow: '0 2px 6px rgba(0,0,0,.7), 0 0 12px rgba(236,211,154,.55)',
    whiteSpace: 'nowrap',
  });
  el.textContent = testo;
  anima(
    el,
    [
      { transform: 'translate(-50%, -50%) scale(0.6)', opacity: 0 },
      { transform: 'translate(-50%, -90%) scale(1.15)', opacity: 1, offset: 0.25 },
      { transform: 'translate(-50%, -150%) scale(1)', opacity: 1, offset: 0.7 },
      { transform: 'translate(-50%, -190%) scale(0.95)', opacity: 0 },
    ],
    { duration: 1300, delay: ritardo, easing: 'cubic-bezier(.2,.7,.3,1)' },
  );
}

/**
 * La durata della fascia «Tocca a te» e la parte di essa in cui il testo sta FERMO e si legge (I-807: «troppo veloce, non riesco a leggerla»).
 * Prima: 1400 ms con entrata al 22% e uscita al 72% = ~700 ms di testo fermo. Ora 3400 ms, entrata al 10% e uscita al 90% = 2720 ms fermi (una riga breve, ≥ 2,5 s).
 * Non copre i comandi: l'elemento ha `pointer-events: none` (`nuovo`).
 */
export const BANNER_TURNO_MS = 3400;
export const BANNER_TURNO_ENTRATA = 0.1;
export const BANNER_TURNO_USCITA = 0.9;

/** Il cambio di turno quando tocca a me: una fascia di luce teal attraversa lo schermo con «Tocca a te». */
export function bannerTurno(testo: string) {
  const el = nuovo({
    left: '0',
    right: '0',
    top: '36%',
    height: '58px',
    display: 'grid',
    placeItems: 'center',
    background: 'linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--color-brand) 18%, transparent) 18%, color-mix(in srgb, color-mix(in srgb, var(--color-brand) 25%, black) 88%, transparent) 38%, color-mix(in srgb, color-mix(in srgb, var(--color-brand) 25%, black) 88%, transparent) 62%, color-mix(in srgb, var(--color-brand) 18%, transparent) 82%, transparent 100%)',
    borderTop: '1px solid color-mix(in srgb, color-mix(in srgb, var(--color-brand) 50%, white) 45%, transparent)',
    borderBottom: '1px solid color-mix(in srgb, color-mix(in srgb, var(--color-brand) 50%, white) 45%, transparent)',
    fontFamily: "Georgia, 'Noto Serif', 'Times New Roman', serif",
    fontWeight: '700',
    fontSize: '24px',
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    color: '#eafffd',
    textShadow: '0 0 14px color-mix(in srgb, var(--color-brand) 80%, transparent)',
  });
  el.textContent = testo;
  anima(
    el,
    [
      { transform: 'translateX(-100%)', opacity: 0 },
      { transform: 'translateX(0)', opacity: 1, offset: BANNER_TURNO_ENTRATA },
      { transform: 'translateX(0)', opacity: 1, offset: BANNER_TURNO_USCITA },
      { transform: 'translateX(100%)', opacity: 0 },
    ],
    { duration: BANNER_TURNO_MS, easing: 'cubic-bezier(.25,.8,.3,1)' },
  );
}
